import { useState } from "react";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { KineticHeading, Reveal } from "@/components/site/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const details = [
  { icon: MapPin, label: "Showroom", value: "Residency Road, Srinagar, J&K 190001" },
  { icon: Phone, label: "Call / WhatsApp", value: "+91 98765 43210" },
  { icon: Mail, label: "Email", value: "hello@gulharfurniture.com" },
  { icon: Clock, label: "Hours", value: "Mon–Sat · 10:00 – 20:00" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/inquiries", form);
      toast.success("Message sent. We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32 md:pt-40">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-8">
        <p className="text-xs tracking-[0.4em] uppercase text-primary mb-6">Get in touch</p>
        <KineticHeading
          className="font-serif font-light leading-[0.95] text-5xl sm:text-7xl lg:text-8xl"
          lines={["Let's build", "something lasting."]}
        />

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 mt-20">
          <Reveal className="lg:col-span-5">
            <p className="text-muted-foreground text-lg leading-relaxed max-w-md">
              Visit our Srinagar showroom, or send us a note about a custom commission. We reply to every enquiry personally.
            </p>
            <div className="mt-12 space-y-8">
              {details.map((d) => (
                <div key={d.label} className="flex items-start gap-5" data-testid={`contact-${d.label.toLowerCase().split(" ")[0]}`}>
                  <div className="h-11 w-11 shrink-0 border border-border flex items-center justify-center text-primary">
                    <d.icon size={18} />
                  </div>
                  <div>
                    <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground mb-1">{d.label}</p>
                    <p className="text-foreground">{d.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-7">
            <form onSubmit={submit} className="border border-border bg-card p-6 md:p-10 space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <Input required placeholder="Your name" data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-background border-border h-12" />
                <Input required type="email" placeholder="Email" data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-background border-border h-12" />
              </div>
              <Input placeholder="Phone (optional)" data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-background border-border h-12" />
              <Textarea required placeholder="Tell us what you're looking for…" rows={6} data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-background border-border" />
              <Button type="submit" disabled={submitting} data-testid="contact-submit" className="w-full h-12 bg-primary text-primary-foreground hover:opacity-90 rounded-none tracking-wide">
                {submitting ? "Sending…" : "Send Message"}
              </Button>
            </form>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
